import { useQueryClient } from "react-query";
import { MdOutlineWhatsapp, MdOpenInNew } from "react-icons/md";
import axios from "../../config/axiosInstance";
import { CRM } from "../../config/apiConfig";
import { useApplication } from "../../contexts/ApplicationContext";
import { Badge } from "./Primitives";
import {
  formatDaysCount,
  formatMoney,
  formatNumber,
  formatRelativeDays,
  initialsOf,
  whatsappLink,
} from "./crmHelpers";
import ClientLink from "./ClientLink";
import {
  describeContact,
  renderTemplate,
  templateForQueue,
} from "./contactTemplates";

/*
  Uma linha de trabalho: a conta, o MOTIVO de ela estar ali, e a ação.

  A tabela antiga mostrava oito colunas fixas em todas as filas, então na fila
  de conversão de free apareciam sete "R$ 0,00" (contas free: é sempre zero) e
  o número que explica a fila ficava no meio, em cinza.

  Aqui o destaque é escolhido pela própria regra da fila (`highlight`, vindo do
  servidor) e ocupa a posição de maior peso visual depois do nome. O resto —
  plano, estágio, responsável — vira uma linha de apoio: continua disponível,
  deixa de competir.
*/

const formatHighlight = (row, highlight) => {
  if (!highlight) return null;

  const value = row[highlight.field];
  if (value === null || value === undefined) return null;

  switch (highlight.format) {
    case "days":
      return { value: formatDaysCount(value), label: highlight.label };
    case "daysUntil":
      return { value: formatRelativeDays(row.planExpirationDate), label: highlight.label };
    case "money":
      return { value: formatMoney(value), label: highlight.label };
    case "percent":
      return { value: `${formatNumber(value)}%`, label: highlight.label };
    default:
      return { value: formatNumber(value), label: highlight.label };
  }
};

/*
  Cor do destaque pela urgência do próprio número, não pela fila. Uma
  renovação que vence hoje e uma que vence em sete dias estão na mesma fila e
  não merecem o mesmo peso.
*/
const highlightTone = (row, highlight) => {
  if (!highlight) return "text-slate-900";

  if (highlight.format === "daysUntil") {
    if (row.daysToExpire < 0) return "text-rose-600";
    if (row.daysToExpire <= 2) return "text-rose-600";
    if (row.daysToExpire <= 7) return "text-amber-600";
    return "text-slate-900";
  }

  if (highlight.field === "daysSinceLastActivity" && row.daysSinceLastActivity > 60) {
    return "text-rose-600";
  }

  return "text-slate-900";
};

const WorkRow = ({ row, highlight, queueId, queueLabel, isActive, onOpen }) => {
  const queryClient = useQueryClient();
  const { user } = useApplication();

  /*
    O atalho abre o WhatsApp com a mensagem da fila, registra o contato e abre o painel
    da conta, que pergunta como foi. Sem registrar, a conta seguiria na fila como se
    ninguém tivesse falado com ela.
  */
  const template = templateForQueue(queueId);
  const message = template
    ? renderTemplate(template, {
        account: row,
        paymentUrl: row.paymentUrl,
        agentName: user?.name,
      })
    : undefined;
  const phone = whatsappLink(row.phoneNumber, message);
  const hero = formatHighlight(row, highlight);

  const registerContact = (event) => {
    event.stopPropagation();

    axios
      .post(`${CRM.INTERACTIONS}/${row.id}/interactions`, {
        type: "WHATSAPP",
        body: describeContact(template, message),
      })
      .then(() => {
        queryClient.invalidateQueries(["crm", "client", String(row.id)]);
        queryClient.invalidateQueries(["crm", "queues"]);
      })
      // O WhatsApp já abriu. Sem o registro, o painel segue aberto para anotar à mão.
      .catch(() => {})
      // O painel abre depois de gravar: aberto antes, ele poderia ler o histórico ainda
      // sem este contato, e a pergunta de como foi não apareceria.
      .finally(() => onOpen(row.id));
  };

  return (
    <div
      className={`group flex items-center gap-3 border-b border-slate-100 px-4 py-3 transition last:border-0 ${
        isActive ? "bg-pink-50/60" : "hover:bg-slate-50/80"
      }`}
    >
      <button
        type="button"
        onClick={() => onOpen(row.id)}
        className="flex min-w-0 flex-1 items-center gap-3 text-left"
      >
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-pink-50 text-[11px] font-bold text-pink-700">
          {initialsOf(row.name)}
        </span>

        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-bold text-slate-900">
            {row.name}
          </span>
          <span className="flex min-w-0 items-center gap-1 text-[11px] text-slate-500">
            {!row.lastContactAt ? (
              <span
                title="Nunca recebeu contato"
                className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400"
              />
            ) : null}
            <span className="truncate">
            {queueLabel ? `${queueLabel} · ` : ""}
            {row.planId}
            {row.ownerName ? ` · ${row.ownerName}` : " · sem responsável"}
            {row.lastContactAt
              ? ` · falado ${formatRelativeDays(row.lastContactAt)}`
              : " · nunca contatada"}
            </span>
          </span>
        </span>
      </button>

      {hero ? (
        <span className="shrink-0 text-right">
          <span
            className={`block text-sm font-bold leading-tight ${highlightTone(
              row,
              highlight
            )}`}
          >
            {hero.value}
          </span>
          <span className="block text-[10px] uppercase tracking-wider text-slate-400">
            {hero.label}
          </span>
        </span>
      ) : null}

      <span className="flex shrink-0 items-center gap-1">
        {row.openTasks > 0 ? <Badge tone="info">{row.openTasks}</Badge> : null}
        {phone ? (
          <a
            href={phone}
            target="_blank"
            rel="noreferrer"
            title="Abrir WhatsApp e registrar o contato"
            onClick={registerContact}
            className="rounded-lg p-1.5 text-slate-300 transition hover:bg-emerald-50 hover:text-emerald-600"
          >
            <MdOutlineWhatsapp size={18} />
          </a>
        ) : null}
        <ClientLink
          hideWhenBlocked
          to={`/clientes/${row.id}`}
          title="Abrir ficha completa"
          onClick={(event) => event.stopPropagation()}
          className="rounded-lg p-1.5 text-slate-300 transition hover:bg-pink-50 hover:text-pink-600"
        >
          <MdOpenInNew size={16} />
        </ClientLink>
      </span>
    </div>
  );
};

export default WorkRow;
